import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

interface SyncSfResult {
  ok: boolean;
  cohortSize: number;
  opportunityCount: number;
  callsScanned: number;
  callsMatched: number;
  callsSkipped: number;
  callsProcessed: number;
  callsFailed: number;
  emailsWritten: number;
  accountIds: string[];
}

/**
 * Compact inline CTA used in the Pipeline page empty state. Calls the same
 * `syncSalesforceNow` callable as `SyncSalesforceCohortButton`, but renders
 * as a single button with status text — appropriate for a contextual prompt
 * inside the deal detail pane rather than a standalone settings card.
 */
export function SyncPipelineButton({
  lookbackDays = 7,
}: {
  lookbackDays?: number;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SyncSfResult | null>(null);

  const onClick = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fn = httpsCallable<{ lookbackDays: number }, SyncSfResult>(
        functions,
        'syncSalesforceNow',
      );
      const res = await fn({ lookbackDays });
      setResult(res.data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex flex-col items-center gap-2">
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        className="btn-primary"
      >
        {busy ? 'Syncing Salesforce…' : 'Sync Salesforce cohort'}
      </button>
      {result && (
        <p className="text-xs text-emerald-700">
          Cohort {result.cohortSize} accounts · {result.opportunityCount} opps
          · processed {result.callsProcessed} calls.
        </p>
      )}
      {error && <p className="text-xs text-rose-700">{error}</p>}
    </div>
  );
}
